import React from "react";
import { Info, AlertTriangle, Lightbulb } from "lucide-react";

const bold = (text) =>
  text.split(/(\*\*[^*]+\*\*)/g).map((part, i) =>
    part.startsWith("**") && part.endsWith("**")
      ? <strong key={i} className="text-ink-primary font-semibold">{part.slice(2, -2)}</strong>
      : part);

const CALLOUTS = {
  NOTE: { icon: Info, cls: "border-cyan-500/40 bg-cyan-500/10 text-cyan-200" },
  WARNING: { icon: AlertTriangle, cls: "border-amber-500/40 bg-amber-500/10 text-amber-200" },
  TIP: { icon: Lightbulb, cls: "border-emerald-500/40 bg-emerald-500/10 text-emerald-200" },
};

export default function ArticleRenderer({ content }) {
  const lines = (content || "").split("\n");
  const out = [];
  let list = null; // {type: 'ol'|'ul', items: []}

  const flush = (key) => {
    if (!list) return;
    const Tag = list.type === "ol" ? "ol" : "ul";
    out.push(
      <Tag key={`l${key}`} className={`${list.type === "ol" ? "list-decimal" : "list-disc"} pl-6 space-y-1.5 text-sm text-ink-secondary`}>
        {list.items.map((it, i) => <li key={i}>{bold(it)}</li>)}
      </Tag>
    );
    list = null;
  };

  lines.forEach((raw, i) => {
    const line = raw.trimEnd();
    const t = line.trim();
    if (!t) { flush(i); return; }
    if (t.startsWith("## ")) {
      flush(i);
      out.push(<h2 key={i} className="font-display text-lg font-bold text-ink-primary mt-6 first:mt-0">{t.slice(3)}</h2>);
      return;
    }
    if (t.startsWith("### ")) {
      flush(i);
      out.push(<h3 key={i} className="font-display text-base font-semibold text-ink-primary mt-4">{t.slice(4)}</h3>);
      return;
    }
    const call = t.match(/^>\s*(NOTE|WARNING|TIP):\s*(.*)$/);
    if (call) {
      flush(i);
      const { icon: Icon, cls } = CALLOUTS[call[1]];
      out.push(
        <div key={i} className={`flex items-start gap-2 rounded-md border px-3 py-2.5 text-xs ${cls}`}>
          <Icon className="h-3.5 w-3.5 mt-0.5 shrink-0" /><span>{bold(call[2])}</span>
        </div>
      );
      return;
    }
    const num = t.match(/^\d+\.\s+(.*)$/);
    if (num) {
      if (!list || list.type !== "ol") { flush(i); list = { type: "ol", items: [] }; }
      list.items.push(num[1]);
      return;
    }
    if (t.startsWith("- ")) {
      if (!list || list.type !== "ul") { flush(i); list = { type: "ul", items: [] }; }
      list.items.push(t.slice(2));
      return;
    }
    flush(i);
    out.push(<p key={i} className="text-sm text-ink-secondary leading-relaxed">{bold(t)}</p>);
  });
  flush("end");
  return <div className="space-y-3">{out}</div>;
}
